import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { isUndefined } from "lodash";
import classNames from "classnames";

/**
 * Dropdown in public (unauthenticated) header.
 * Derives login items from the already prepared public menu items in redux.
 *
 * Expected structure:
 * - top-level group item: { id: 'publicLogin', path: '/prihlaseni', primaryText: 'Přihlásit se jako' }
 * - its children: items with parent === 'publicLogin'
 */
const PublicLoginDropdown = ({
  items,
  onNavigate,
  label = "PŘIHLÁSIT SE JAKO",
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const loginItems = useMemo(() => {
    const children = items
      .filter((i) => i && i.parent === "publicLogin")
      .filter((i) => Boolean(i.path) || Boolean(i.link));

    // keep deterministic order (menu order), but also ensure unique ids/paths
    const seen = new Set();
    return children.filter((i) => {
      const key = i.id || i.path || i.link;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [items]);

  if (!loginItems.length) return null;

  return (
    <div
      className={classNames("publicLoginDropdown", { "is-open": isOpen })}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="publicLoginDropdown__trigger"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {label}
        <span className="publicLoginDropdown__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {isOpen && (
        <div className="publicLoginDropdown__menu" role="menu">
          {loginItems.map((item) => {
            // External link item
            if (!isUndefined(item.link)) {
              return (
                <a
                  key={item.id || item.link}
                  className="publicLoginDropdown__item"
                  href={item.link}
                  role="menuitem"
                >
                  {item.primaryText}
                </a>
              );
            }

            // Route item
            return (
              <button
                key={item.id || item.path}
                type="button"
                className="publicLoginDropdown__item"
                onClick={() => {
                  setIsOpen(false);
                  onNavigate(item);
                }}
                role="menuitem"
              >
                {item.primaryText}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

PublicLoginDropdown.propTypes = {
  items: PropTypes.array.isRequired,
  onNavigate: PropTypes.func.isRequired,
  label: PropTypes.string,
};

export default PublicLoginDropdown;
